/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import { useState, useEffect, memo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Progress from "components/Progress";
import { useMaterialUIController, setPreviewDocument } from "context";

import ServerSideTable from "components/Tables/ServerSideTable";
import ERPAPI from "services/erp";
import CommonFun from "services/common";

function Quotations({ customerID }) {
  const { t } = useTranslation("common");
  const [, dispatch] = useMaterialUIController();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [count, setCount] = useState(0);
  const [orderBy, setOrderBy] = useState("documentId");
  const [order, setOrder] = useState("asc");
  const [documentsMenu, setDocumentsMenu] = useState(null);

  const getQuotations = async () => {
    if (customerID.includes("erp-")) {
      setLoading(true);
      const ERPResponse = await ERPAPI.getCustomerQuotations(
        customerID.replace("erp-", ""),
        page,
        pageSize,
        orderBy,
        order
      );
      if (
        ERPResponse &&
        ERPResponse.data &&
        ERPResponse.data.items &&
        ERPResponse.data.items.length
      ) {
        setQuotations(ERPResponse.data.items);
        setCount(ERPResponse.data.totalCount);
      } else {
        setQuotations([]);
      }
      setLoading(false);
    } else {
      setQuotations([]);
    }
  };

  useEffect(async () => {
    getQuotations();
  }, [customerID, page, pageSize, orderBy, order]);

  const openDocumentsMenu = (event, row) => {
    setDocumentsMenu({
      position: {
        top: event.clientY,
        left: event.clientX,
      },
      anchorEl: event.currentTarget,
      row,
    });
  };

  const closeDocumentsMenu = () => setDocumentsMenu(null);

  const columns = [
    {
      header: t("Quotation Number"),
      accessor: "documentIdentifier",
      align: "left",
      sort: true,
    },
    {
      header: t("Quotation Date"),
      accessor: "documentDate",
      align: "left",
      sort: true,
      dateFormat: CommonFun.getDateFormat(),
    },
    {
      header: t("Valid Until"),
      accessor: "validUntil",
      align: "left",
      sort: true,
      dateFormat: CommonFun.getDateFormat(),
    },
    {
      header: t("Line Item"),
      accessor: "numberOfLines",
      align: "left",
      sort: true,
    },
    {
      header: t("Net Total"),
      accessor: "nativeNetValue",
      align: "left",
      sort: true,
      currency: "nativeCurrencyIso",
    },
    {
      header: t("Total"),
      accessor: "nativeTotalValue",
      align: "left",
      sort: true,
      currency: "nativeCurrencyIso",
    },
    {
      header: t("Payment Method"),
      accessor: "paymentCondition",
      align: "left",
      sort: true,
    },
    {
      header: t("Status"),
      accessor: "quoteStatus",
      align: "center",
      sort: true,
    },
    {
      header: t("Documents"),
      accessor: "documents",
      align: "center",
      sort: false,
      render: (row) => (
        <MDBox sx={{ position: "relative" }}>
          <IconButton
            color="black"
            size="small"
            aria-describedby={`menu_${row.documentId}`}
            onClick={(event) => {
              openDocumentsMenu(event, row);
            }}
            disabled={!row.orderDocuments || !row.orderDocuments.length}
          >
            <Icon>picture_as_pdf</Icon>
          </IconButton>
          <Menu
            id={`menu_documents_${row.documentId}`}
            anchorEl={documentsMenu !== null ? documentsMenu.anchorEl : undefined}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            open={!!(documentsMenu && row.documentId === documentsMenu.row.documentId)}
            onClose={closeDocumentsMenu}
          >
            {row.orderDocuments &&
              row.orderDocuments.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={() => {
                    setPreviewDocument(dispatch, {
                      isImage: false,
                      documentIdentifier: item.documentIdentifier,
                      name: item.documentType,
                      dmsDocumentId: item.dmsDocumentId,
                    });
                  }}
                  sx={{
                    padding: 1,
                    marginBottom: 0.5,
                    border:
                      item.documentIdentifier && item.dmsDocumentId
                        ? "1px solid #1A73E8"
                        : "1px solid #000000",
                  }}
                  disabled={!item.documentIdentifier || !item.dmsDocumentId}
                >
                  <MDBox sx={{ display: "flex", width: "100%" }}>
                    <MDBox
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <MDTypography
                        variant="button"
                        color={item.documentIdentifier && item.dmsDocumentId ? "info" : "black"}
                      >
                        {item.documentType}
                      </MDTypography>
                      <MDTypography
                        variant="button"
                        color={item.documentIdentifier && item.dmsDocumentId ? "info" : "black"}
                      >
                        {item.documentIdentifier}
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        paddingLeft: 1,
                        paddingRight: 1,
                      }}
                    >
                      <MDTypography variant="caption" color="black">
                        {moment.utc(item.documentDate).format(CommonFun.getDateFormat())}
                      </MDTypography>
                      <MDTypography variant="caption" color="black">
                        {CommonFun.displayCurrency(item.nativeTotalValue, item.nativeCurrencyIso)}
                      </MDTypography>
                    </MDBox>
                    <MDBox sx={{ display: "flex", alignItems: "center", padding: 1 }}>
                      <MDTypography color="black">
                        <Icon>picture_as_pdf</Icon>
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MenuItem>
              ))}
          </Menu>
        </MDBox>
      ),
      getCellProps: () => ({
        // onClick: () => {}
        style: {
          cursor: "default",
        },
      }),
    },
  ];

  const onPageChange = (event, newPage) => {
    setPage(newPage);
  };

  const onRowsPerPageChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  // eslint-disable-next-line no-console
  console.log("render customer", customerID, "Quotations");

  return (
    <MDBox bgColor="light">
      <Progress loading={loading} isFull />
      <ServerSideTable
        fixedTable
        rowsPerPage={pageSize}
        page={page}
        columns={columns}
        data={quotations}
        count={count}
        order={order}
        orderBy={orderBy}
        onRequestSort={(key) => {
          const isAsc = orderBy === key && order === "asc";
          setOrder(isAsc ? "desc" : "asc");
          setOrderBy(key);
        }}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        getRowProps={(row) => ({
          onClick: () => {
            CommonFun.onClickQuotation(row.documentIdentifier);
          },
          style: {
            cursor: "pointer",
          },
        })}
      />
      <MDBox sx={{ display: "flex", justifyContent: "flex-end" }}>
        {quotations.length ? (
          <MDBox pr={3}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              {page === 0 ? page + 1 : page * pageSize + 1} {t("to")}{" "}
              {page === 0 ? quotations.length : pageSize * page + quotations.length}
            </MDTypography>
          </MDBox>
        ) : null}
        <MDBox>
          <IconButton
            aria-label="Next"
            disabled={page === 0}
            onClick={() => {
              setPage(page - 1);
              getQuotations(page - 1);
            }}
          >
            <Icon>keyboard_arrow_left</Icon>
          </IconButton>
        </MDBox>
        <MDBox>
          <IconButton
            aria-label="Next"
            disabled={!quotations.length || quotations.length < pageSize}
            onClick={() => {
              setPage(page + 1);
              getQuotations(page + 1);
            }}
          >
            <Icon>keyboard_arrow_right</Icon>
          </IconButton>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

export default memo(Quotations);
